import { FormikValues } from "formik";
import { useCallback, useMemo, useRef } from "react";
import { cleanFilterRules, isConditionSatisfiedMemoized } from "../helpers/conditions";
import { FormConfigApplicationStructureItem, FormConfigItem, FormItemType } from "../types/form";
import { Filter, FilterField } from "@smartsuite/types";
import { useFormContext } from "./useFormContext";

export type FieldVisibilityCallback = (fieldSlug: string, visitedFields?: string[]) => boolean;

export interface UseFormFieldVisibilityResult {
  checkFieldVisibility: FieldVisibilityCallback;
}

export const useFormFieldVisibility = (
  formItems: FormConfigItem[],
  formValues: FormikValues,
  appFieldMap: Map<string, FormConfigApplicationStructureItem>
): UseFormFieldVisibilityResult => {
  const { preferences, config } = useFormContext();

  // The server gates the same rule on this flag, so the field a form shows or hides has to
  // agree with what the grid returns for the identical condition.
  const evaluationOptions = useMemo(
    () => ({ newDateVocabulary: config.feature_flags?.date_filter_new_options === true }),
    [config.feature_flags?.date_filter_new_options]
  );

  // fields-logic's resolveFieldBySlug reads boundary subfields from `field.nested`, but the
  // form config API nests them under `field.params.nested`. Without lifting them, a date-range
  // condition ("<slug>.from_date") fails to resolve the nested field and DateRangeFieldLogic
  // recurses into itself forever. Lift `params.nested` to the top level so it resolves.
  const normalizedAppFieldMap = useMemo(() => {
    const normalized = new Map(appFieldMap);
    appFieldMap.forEach((field, slug) => {
      const nested = (field.params as { nested?: FormConfigApplicationStructureItem[] })?.nested;
      if (nested && !(field as { nested?: unknown }).nested) {
        normalized.set(slug, { ...field, nested } as FormConfigApplicationStructureItem);
      }
    });
    return normalized;
  }, [appFieldMap]);

  // Cache for visibility results to avoid recalculating
  const visibilityCache = useRef<Map<string, boolean>>(new Map());

  // Array containing all form fields and sections (and fields within it) to calculate
  // their conditions.
  const formConditionalItems = useMemo(() => {
    const items: FormConfigItem[] = [];
    formItems?.forEach((item) => {
      if (item.type === FormItemType.section) {
        items.push(item);
        items.push(...(item.params.items ?? []).filter((i) => i.type !== FormItemType.section));
      } else {
        items.push(item);
      }
    });
    return items;
  }, [formItems]);

  // This map contains the valid conditions for each field slug
  const validConditionsMap = useMemo(
    () =>
      formConditionalItems.reduce<Record<string, Filter | null>>((acc, item) => {
        acc[item.slug] = null;
        const conditions = item.params?.conditions;

        // If the rule is disabled or empty, then it won't got to the map
        if (!conditions?.enabled || !conditions?.object) return acc;

        // If the rule has no valid entries, then it won't got to the map
        const validConditions = cleanFilterRules(conditions.object.fields);
        if (!validConditions.length) return acc;

        // Other rules are added to the map for the field slug
        acc[item.slug] = { ...conditions.object, fields: validConditions };
        return acc;
      }, {}),
    [formConditionalItems]
  );

  // Create a flat list of all items once
  const allItems = useMemo(() => {
    return formItems?.reduce<FormConfigItem[]>((acc, item) => {
      if (item.type === FormItemType.section) {
        return acc.concat(item.params.items ?? []);
      }
      return acc.concat(item);
    }, []);
  }, [formItems]);

  // Create a map for faster field lookup
  const fieldMap = useMemo(() => {
    return new Map(allItems?.map((item) => [item.slug, item]));
  }, [allItems]);

  // Maps a field slug to the slug of the section it lives in. A field inside a
  // section has no conditions of its own — the section owns them — so its
  // visibility must follow the section's (BUG-20455).
  const parentSectionBySlug = useMemo(() => {
    const map = new Map<string, string>();
    formItems?.forEach((item) => {
      if (item.type === FormItemType.section) {
        (item.params.items ?? []).forEach((child) => {
          if (child.type !== FormItemType.section) {
            map.set(child.slug, item.slug);
          }
        });
      }
    });
    return map;
  }, [formItems]);

  const checkFieldVisibility = useCallback(
    (fieldSlug: string, visitedFields: string[] = []): boolean => {
      // Return false if field has already been visited to prevent infinite recursion
      if (visitedFields.includes(fieldSlug)) {
        return false;
      }

      // Check cache first
      const cacheKey = `${fieldSlug}-${JSON.stringify(visitedFields)}`;
      const cachedResult = visibilityCache.current.get(cacheKey);
      if (cachedResult !== undefined) {
        return cachedResult;
      }

      // Puts the current field in the list to prevent infinite recursion
      visitedFields.push(fieldSlug);

      const currentField = fieldMap.get(fieldSlug);

      if (currentField?.params?.hidden && visitedFields.length === 1) {
        visibilityCache.current.set(cacheKey, false);
        return false;
      }

      // A field inside a section inherits the section's visibility: when the
      // section is hidden the child is effectively hidden, so any downstream
      // field conditioned on that child must hide too (BUG-20455).
      const parentSectionSlug = parentSectionBySlug.get(fieldSlug);
      if (parentSectionSlug && !checkFieldVisibility(parentSectionSlug, [...visitedFields])) {
        visibilityCache.current.set(cacheKey, false);
        return false;
      }

      // If field has no valid conditions to be parsed, then it's visible by default
      const validConditions = validConditionsMap[fieldSlug];

      if (!validConditions) {
        visibilityCache.current.set(cacheKey, true);
        return true;
      }

      const checkConditions = (fields: FilterField[], checkVisibility = false): boolean => {
        const checkField = (filterField: FilterField): boolean => {
          // A condition on a date-range boundary references a dotted path
          // ("<slug>.from_date" / "<slug>.to_date"), but form items, Formik values and
          // appFieldMap are keyed by the base slug. Resolve the base field everywhere —
          // including the visibility of the condition's source field, so a hidden date
          // range hides its dependents like any other field. DateRangeFieldLogic derives
          // the boundary from filterField.field.
          const baseSlug = filterField.field.split(".")[0];

          // Check if the field involved in the condition is visible
          if (!checkFieldVisibility(baseSlug, [...visitedFields])) {
            return false;
          }

          if (checkVisibility) {
            return checkFieldVisibility(baseSlug, [...visitedFields]);
          }

          const fieldValue = formValues[baseSlug];
          const fieldDefinition = normalizedAppFieldMap.get(baseSlug);
          return isConditionSatisfiedMemoized(
            filterField,
            fieldValue,
            fieldDefinition,
            false,
            preferences,
            evaluationOptions
          );
        };

        return validConditions.operator === "and"
          ? fields.every(checkField)
          : fields.some(checkField);
      };

      const result =
        checkConditions(validConditions.fields) && checkConditions(validConditions.fields, true);
      visibilityCache.current.set(cacheKey, result);
      return result;
    },
    [
      normalizedAppFieldMap,
      formValues,
      validConditionsMap,
      fieldMap,
      parentSectionBySlug,
      preferences,
      evaluationOptions,
    ]
  );

  // Clear cache when formValues change
  useMemo(() => {
    visibilityCache.current.clear();
  }, [formValues]);

  return {
    checkFieldVisibility,
  };
};
